// 자동 퀘스트 진행(순수 로직, DOM 없음): 목표(NPC·던전 문·다음 전투방)까지 길을 찾아 월드 방향 입력을 만든다.
// 마을: NPC 앞에 서면 대화(상호작용)를 한 번 누르고, 문으로 가면 던전에 들어간 뒤 첫 전투방까지 이어서 간다.
// 던전: 다음 전투방까지 걷고, 전투 중에는 손을 놓고 기다리다가 방을 정리하면 다음 방으로 간다. 보스방에 들어가면 끝난다.
// 걷는 동안 사용자가 직접 이동하면 바로 멈춘다(전투 중 대기일 때의 조작은 멈추지 않는다). 시뮬레이션 상태는 바꾸지 않는다.
import { HERO } from '../shared/config';
import { inRect, type MapGrid, type RegionId } from '../shared/levels';
import { NavPlanner, type NavWorld } from '../shared/nav';
import { REGIONS } from '../shared/regions';
import { REBIRTH_GATE } from '../shared/rebirth';
import { townNpcByRole, townNpcs, type NpcRole } from '../shared/rpg';
import { MobState, Scene, type GameView } from '../shared/types';

export type AutoTarget = { readonly kind: 'npc'; readonly role: NpcRole } | { readonly kind: 'door'; readonly region: RegionId } | { readonly kind: 'dungeon' } | { readonly kind: 'gate' };
export type AutoStatus = 'off' | 'walking' | 'talking' | 'combat';
/** 끝난 이유(화면 안내용) */
export type AutoEnd = 'arrived' | 'manual' | 'stuck' | 'boss' | 'cleared' | 'scene' | 'cancel' | 'menu';

/** 방 입장 판정 여유(flow.ts ROOM_ENTER_INSET 2m)보다 조금 더 안쪽까지 들어간다 */
const ROOM_GOAL_INSET = 2.6;
const ARRIVE_R = 0.45;
const STUCK_MS = 1200, STUCK_MOVE = 0.25, STUCK_TRIES = 3;
/** 길이 없을 때(방을 막 정리해 문이 열리는 중 등) 0.4초마다 다시 찾고, 이만큼 연속 실패하면 멈춘다 */
const PLAN_RETRY_MS = 400, PLAN_FAILS = 10;

/**
 * 전투 정체(C11): 전투 중 이만큼(ms) 처치가 없으면 남은 몹에게 길을 찾아 다가간다(몹이 기둥·낭떠러지 뒤에 서서 서로 못 만나는 정지 방지).
 * 다가가는 동안 SEEK_REPLAN_MS마다 가장 가까운 몹으로 다시 찾고, SEEK_NEAR m 안에 들면 멈춰 기다린다.
 */
const COMBAT_STALL_MS = 4000, SEEK_REPLAN_MS = 1000, SEEK_NEAR = 1.8;
/** 걷다 막혔을 때 사방이 트인 안전 지점(영웅에게서 SAFE_MIN m 이상)으로 비켜 섰다가 다시 찾는다. 이만큼 해도 막히면 멈춘다 */
const SAFE_MIN = 1.5, SAFE_HOPS = 2;

/** 윤회의 문 도착 판정(m): 상호작용 반경 안. 길찾기 목표(반경 -0.6m)보다 0.1m 느슨하게 */
const GATE_GOAL_R = REBIRTH_GATE.interactRadius - 0.6, GATE_ARRIVE_R = REBIRTH_GATE.interactRadius - 0.5;
/** 마을 워프를 다시 요청하기까지(ms)와 최대 요청 수 */
const TRAVEL_RETRY_MS = 1500, TRAVEL_TRIES = 3;

/** travel > 0 이면 그 마을로 워프 명령을 한 번 보낸다(AutoQuestUi가 GameCommand로 바꾼다) */
export interface AutoOut { x: number; z: number; interact: boolean; travel?: number }

/**
 * 문 자동 이동이 던전까지 이어질 수 있는지: 그 문(지역)의 일반 입장일 때만. 일일 던전·탑 등 다른 입장(패널에서 들어감)이나
 * 다른 지역으로 들어가면 「자동 이동 중」 알약이 던전 안에 남지 않게 멈춘다.
 */
export function doorLeadsHere(region: string, stageId: string, stageKind: string): boolean {
  if (stageKind !== 'normal' && stageKind !== 'mid' && stageKind !== 'final' && stageKind !== 'free') return false;
  // 하드·헬 난이도 문은 같은 지역의 h<n>-…·x<n>-… 스테이지로 들어간다(GameSim.doorStage, PR #273 Codex P2 · C10)
  return stageId === '' || stageId.startsWith(region + '-') || (region.startsWith('r') && (stageId.startsWith('h' + region.slice(1) + '-') || stageId.startsWith('x' + region.slice(1) + '-')));
}

export class AutoPilot {
  target: AutoTarget | null = null;
  /** 길을 찾은 마을(NPC 몸 충돌 자리) */
  private town = 1;
  /** 마을 NPC 몸체: 영웅 중심이 들어가면 안 되는 원(영웅 반지름 + NPC 반지름 + 여유 0.1m). 그 마을에 선 NPC만 */
  private readonly npcBlocked = (x: number, z: number): boolean => {
    const list = townNpcs(this.town);
    for (let i = 0; i < list.length; i++) {
      const n = list[i], dx = x - n.x, dz = z - n.z, r = HERO.radius + n.radius + 0.1;
      if (dx * dx + dz * dz < r * r) return true;
    }
    return false;
  };
  status: AutoStatus = 'off';
  /** 마지막으로 끝난 이유. 새로 시작하면 null */
  ended: AutoEnd | null = null;
  /** 경로가 바뀔 때마다 1 증가(렌더 점선 갱신용) */
  routeVersion = 0;
  readonly nav = new NavPlanner();
  cursor = 0;
  private serial = -1;
  private planned = false;
  private checkAt = 0;
  private checkX = 0;
  private checkZ = 0;
  private tries = 0;
  private planFails = 0;
  private retryAt = 0;
  private talkWait = 0;
  private travelTries = 0;
  private travelAt = -1e9;
  /** 전투 정체 감지: 마지막으로 본 살아 있는 몹 수와 진척(처치·직접 조작) 시각 */
  private combatAlive = -1;
  private combatAt = 0;
  private seekAt = -1e9;
  /** 안전 지점으로 비켜 서는 중이면 true(도착하면 원래 목표로 다시 찾는다) */
  private unstick = false;
  private safeHops = 0;
  /** 전투 정체로 남은 몹에게 길을 다시 찾은 횟수(1초마다 재계획, 계측용) */
  seeks = 0;
  /** 안전 지점으로 비켜 선 횟수(계측용) */
  unsticks = 0;
  /** 계획 때마다 지금 맵 격자·문 상태로 채운다 */
  private readonly world: { grid: MapGrid; gateOpen: ArrayLike<number>; radius: number; blocked?: (x: number, z: number) => boolean } =
    { grid: null as unknown as MapGrid, gateOpen: [], radius: HERO.radius };
  private village = false;

  start(t: AutoTarget, v: GameView): void {
    this.target = t;
    this.ended = null;
    this.status = 'walking';
    this.planned = false;
    this.tries = 0;
    this.planFails = 0;
    this.travelTries = 0;
    this.travelAt = -1e9;
    this.unstick = false;
    this.safeHops = 0;
    this.combatAlive = -1;
    this.serial = v.flow.sceneSerial;
  }

  stop(reason: AutoEnd): void {
    if (!this.target && this.status === 'off') return;
    this.target = null;
    this.status = 'off';
    this.ended = reason;
    this.planned = false;
    this.nav.count = 0;
    this.routeVersion++;
  }

  get active(): boolean { return this.target !== null; }

  /** 화면에 보일 목적지 이름 */
  label(v: GameView): string {
    const t = this.target;
    if (!t) return '';
    if (t.kind === 'npc') { const n = townNpcByRole(v.flow.town, t.role); return n ? n.title + ' ' + n.name : ''; }
    if (t.kind === 'door') return REGIONS[t.region].name;
    if (t.kind === 'gate') return '윤회의 문';
    const i = this.nextRoom(v);
    return i >= 0 ? v.map.rooms[i].name : v.map.name;
  }

  /**
   * 매 렌더 프레임(또는 틱) 1회. manualMove = 사용자가 지금 이동 입력 중인지. out에 월드 방향과 상호작용 누름을 쓴다.
   */
  update(v: GameView, nowMs: number, manualMove: boolean, out: AutoOut): void {
    out.x = 0; out.z = 0; out.interact = false; out.travel = 0;
    const t = this.target;
    if (!t) return;
    const f = v.flow;
    // 장면 전환: 문 자동 이동은 던전 첫 전투방까지 이어 간다
    if (f.sceneSerial !== this.serial) {
      this.serial = f.sceneSerial;
      if (t.kind === 'door' && f.scene === Scene.Dungeon && doorLeadsHere(t.region, f.stageId, f.stageKind)) { this.target = { kind: 'dungeon' }; this.planned = false; this.tries = 0; }
      else if (t.kind === 'gate' && f.scene === Scene.Village) { this.planned = false; this.tries = 0; } // 마을 워프로 장면이 다시 열려도 이어 간다
      else { this.stop('scene'); return; }
    }
    if (f.scene !== Scene.Village && f.scene !== Scene.Dungeon) { this.stop('scene'); return; }
    if (f.paused) return;
    const tt = this.target!;
    const vv = v.village;
    if (tt.kind === 'npc') {
      // NPC 자리는 마을마다 다르다(S7). 인덱스 = ALL_NPCS 인덱스
      const idx = townNpcByRole(f.town, tt.role)?.index ?? -1;
      if (vv.menuNpc === idx) { this.stop('arrived'); return; }
      if (vv.menuNpc >= 0 || vv.inventoryOpen || vv.stageMapOpen || vv.questOpen) { this.stop('menu'); return; }
      if (vv.nearNpc === idx) {
        if (manualMove && this.status === 'walking') { this.stop('manual'); return; }
        // NPC 앞: 멈추고 대화를 한 번 누른다(창이 안 열리면 0.5초마다 다시)
        if (this.status !== 'talking') { this.status = 'talking'; this.talkWait = 0; this.nav.count = 0; this.routeVersion++; }
        if (this.talkWait-- <= 0) { out.interact = true; this.talkWait = 30; }
        return;
      }
      if (this.status === 'talking') { this.status = 'walking'; this.planned = false; }
    } else if (vv.menuNpc >= 0 || vv.inventoryOpen || vv.stageMapOpen || vv.questOpen) { this.stop('menu'); return; }
    if (tt.kind === 'gate') {
      // 다른 마을이면 먼저 마을①로 워프한다(이동 연출 중에는 기다린다). 세 번 요청해도 못 가면 멈춘다
      if (f.town !== REBIRTH_GATE.town) {
        if (f.travelTo > 0) return;
        if (nowMs - this.travelAt < TRAVEL_RETRY_MS) return;
        if (this.travelTries++ >= TRAVEL_TRIES) { this.stop('stuck'); return; }
        this.travelAt = nowMs; this.planned = false; this.nav.count = 0; this.routeVersion++;
        out.travel = REBIRTH_GATE.town;
        return;
      }
      if (f.travelTo > 0) return;
      if (Math.hypot(v.hero.x - REBIRTH_GATE.x, v.hero.z - REBIRTH_GATE.z) <= GATE_ARRIVE_R) { this.stop('arrived'); return; }
    }
    if (tt.kind === 'dungeon') {
      const room = f.roomIndex >= 0 ? v.map.rooms[f.roomIndex] : undefined;
      if (room && f.roomState[f.roomIndex] === 1) {
        if (room.kind === 'boss') { this.stop('boss'); return; }
        // 전투 중: 손을 놓고 기다린다(이때의 직접 조작은 자동 진행을 끄지 않는다).
        // 처치 없이 COMBAT_STALL_MS가 지나면 남은 몹에게 길을 찾아 다가간다(지형 뒤에 선 몹과 서로 못 만나는 정지 방지)
        if (this.status !== 'combat') { this.status = 'combat'; this.nav.count = 0; this.routeVersion++; this.combatAlive = v.mobs.aliveCount; this.combatAt = nowMs; }
        this.combatStep(v, nowMs, manualMove, out);
        return;
      }
      if (this.status === 'combat') { this.status = 'walking'; this.planned = false; this.tries = 0; this.unstick = false; this.safeHops = 0; }
      if (this.nextRoom(v) < 0) { this.stop('cleared'); return; }
    }
    if (manualMove) { this.stop('manual'); return; }
    const h = v.hero;
    if (!this.planned) {
      if (nowMs < this.retryAt) return;
      if (!this.replan(v)) {
        if (++this.planFails >= PLAN_FAILS) this.stop('stuck');
        else this.retryAt = nowMs + PLAN_RETRY_MS;
        return;
      }
      this.planFails = 0;
      this.checkAt = nowMs + STUCK_MS; this.checkX = h.x; this.checkZ = h.z;
    }
    // 막힘: 1.2초에 25cm도 못 가면 다시 찾는다. 세 번 연속이면 사방이 트인 안전 지점으로 비켜 섰다가 다시 찾고(최대 SAFE_HOPS번), 그래도 막히면 멈춘다
    if (nowMs >= this.checkAt) {
      if (Math.hypot(h.x - this.checkX, h.z - this.checkZ) < STUCK_MOVE) {
        if (++this.tries >= STUCK_TRIES) {
          if (this.safeHops >= SAFE_HOPS || !this.planSafe(v)) { this.stop('stuck'); return; }
          this.safeHops++; this.unsticks++; this.tries = 0;
        } else if (!(this.unstick ? this.planSafe(v) : this.replan(v))) { this.stop('stuck'); return; }
      } else this.tries = 0;
      this.checkAt = nowMs + STUCK_MS; this.checkX = h.x; this.checkZ = h.z;
    }
    const nav = this.nav;
    while (this.cursor < nav.count - 1 && Math.hypot(nav.xs[this.cursor] - h.x, nav.zs[this.cursor] - h.z) < ARRIVE_R) this.cursor++;
    if (this.cursor >= nav.count) return;
    const dx = nav.xs[this.cursor] - h.x, dz = nav.zs[this.cursor] - h.z, d = Math.hypot(dx, dz);
    if (d < 0.05 || (this.unstick && this.cursor >= nav.count - 1 && d < ARRIVE_R)) {
      // 마지막 점에 섰는데 목표 판정이 아직이면(문 사각형 경계 등) 다시 찾는다. 안전 지점에 닿았으면 원래 목표로 돌아간다
      this.planned = false;
      this.unstick = false;
      return;
    }
    out.x = dx / d; out.z = dz / d;
  }

  /** 목적지 표식 자리(NPC 머리 위·문 가운데·방 가운데). 없으면 false */
  goalPoint(v: GameView, out: { x: number; z: number }): boolean {
    const t = this.target;
    if (!t) return false;
    if (t.kind === 'gate') { if (v.flow.town !== REBIRTH_GATE.town) return false; out.x = REBIRTH_GATE.x; out.z = REBIRTH_GATE.z; return true; }
    if (t.kind === 'npc') { const n = townNpcByRole(v.flow.town, t.role); if (!n) return false; out.x = n.x; out.z = n.z; return true; }
    const room = t.kind === 'door' ? undefined : v.map.rooms[this.nextRoom(v)];
    const rc = t.kind === 'door' ? v.map.exits.find((e) => e.to === t.region)?.rect : room?.area;
    if (!rc) return false;
    // 스테이지 지도(S2)의 방은 가운데가 구멍·못일 수 있어 지도가 정한 대표 지점(focus)을 쓴다
    if (room?.focus) { out.x = room.focus.x; out.z = room.focus.z; return true; }
    out.x = (rc.x0 + rc.x1) / 2; out.z = (rc.z0 + rc.z1) / 2;
    return true;
  }

  /** 다음에 들어갈 전투방(대기 상태이고 앞 방을 정리했다), 없으면 -1 */
  nextRoom(v: GameView): number {
    const rs = v.flow.roomState;
    for (let i = 0; i < v.map.rooms.length; i++) if (rs[i] === 0 && (i === 0 || rs[i - 1] === 2)) return i;
    return -1;
  }

  /** 전투 중 한 프레임: 진척(처치·직접 조작)이 있으면 기다리고, COMBAT_STALL_MS 동안 없으면 가장 가까운 몹에게 다가간다 */
  private combatStep(v: GameView, nowMs: number, manualMove: boolean, out: AutoOut): void {
    const alive = v.mobs.aliveCount;
    if (alive < this.combatAlive || manualMove) { this.combatAt = nowMs; if (this.nav.count) { this.nav.count = 0; this.routeVersion++; } }
    this.combatAlive = alive;
    if (manualMove || nowMs - this.combatAt < COMBAT_STALL_MS) return;
    const h = v.hero;
    if (nowMs >= this.seekAt) {
      this.seekAt = nowMs + SEEK_REPLAN_MS;
      const m = this.nearestMob(v);
      if (m < 0) { this.nav.count = 0; return; }
      const mx = v.mobs.x[m], mz = v.mobs.z[m];
      if (Math.hypot(mx - h.x, mz - h.z) <= SEEK_NEAR) { if (this.nav.count) { this.nav.count = 0; this.routeVersion++; } return; }
      this.fillWorld(v);
      const r2 = SEEK_NEAR * SEEK_NEAR;
      if (this.nav.plan(this.world, h.x, h.z, (x, z) => (x - mx) * (x - mx) + (z - mz) * (z - mz) <= r2)) this.seeks++;
      this.cursor = 0;
      this.routeVersion++;
    }
    const nav = this.nav;
    while (this.cursor < nav.count - 1 && Math.hypot(nav.xs[this.cursor] - h.x, nav.zs[this.cursor] - h.z) < ARRIVE_R) this.cursor++;
    if (this.cursor >= nav.count) return;
    const dx = nav.xs[this.cursor] - h.x, dz = nav.zs[this.cursor] - h.z, d = Math.hypot(dx, dz);
    if (d < ARRIVE_R && this.cursor >= nav.count - 1) { nav.count = 0; this.routeVersion++; return; }
    out.x = dx / d; out.z = dz / d;
  }

  /** 길로 따지지 않은 가장 가까운 살아 있는 몹(등장·처치 중 제외), 없으면 -1 */
  private nearestMob(v: GameView): number {
    const m = v.mobs, h = v.hero;
    let best = -1, bd = Infinity;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning) continue;
      const d = (m.x[i] - h.x) * (m.x[i] - h.x) + (m.z[i] - h.z) * (m.z[i] - h.z);
      if (d < bd) { bd = d; best = i; }
    }
    return best;
  }

  private fillWorld(v: GameView): void {
    const w = this.world;
    w.grid = v.grid; w.gateOpen = v.flow.gateOpen; w.radius = HERO.radius;
    this.village = v.flow.scene === Scene.Village;
    this.town = v.flow.town;
    w.blocked = this.village ? this.npcBlocked : undefined;
  }

  /** 막혔을 때: 영웅에게서 SAFE_MIN m 이상 떨어지고 사방 1m가 모두 트인 가장 가까운 칸으로 길을 찾는다 */
  private planSafe(v: GameView): boolean {
    this.fillWorld(v);
    const w = this.world, nav = this.nav, hx = v.hero.x, hz = v.hero.z, r2 = SAFE_MIN * SAFE_MIN;
    const ok = nav.plan(w, hx, hz, (x, z) => {
      if ((x - hx) * (x - hx) + (z - hz) * (z - hz) < r2) return false;
      for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) if (!nav.walkable(w, x + dx, z + dz)) return false;
      return true;
    });
    this.cursor = 0;
    this.planned = ok;
    this.unstick = ok;
    this.routeVersion++;
    return ok;
  }

  private replan(v: GameView): boolean {
    const t = this.target!;
    const w = this.world;
    w.grid = v.grid; w.gateOpen = v.flow.gateOpen; w.radius = HERO.radius;
    this.village = v.flow.scene === Scene.Village;
    this.town = v.flow.town;
    w.blocked = this.village ? this.npcBlocked : undefined;
    let goal: (x: number, z: number) => boolean;
    if (t.kind === 'npc') {
      const n = townNpcByRole(v.flow.town, t.role);
      if (!n) return false;
      const r = n.interactRadius - 0.6;
      goal = (x, z) => (x - n.x) * (x - n.x) + (z - n.z) * (z - n.z) <= r * r;
    } else if (t.kind === 'gate') {
      goal = (x, z) => (x - REBIRTH_GATE.x) * (x - REBIRTH_GATE.x) + (z - REBIRTH_GATE.z) * (z - REBIRTH_GATE.z) <= GATE_GOAL_R * GATE_GOAL_R;
    } else if (t.kind === 'door') {
      const exit = v.map.exits.find((e) => e.to === t.region);
      if (!exit) return false;
      goal = (x, z) => inRect(exit.rect, x, z, 0.5);
    } else {
      const i = this.nextRoom(v);
      if (i < 0) return false;
      const area = v.map.rooms[i].area;
      goal = (x, z) => inRect(area, x, z, ROOM_GOAL_INSET);
    }
    const ok = this.nav.plan(w, v.hero.x, v.hero.z, goal);
    this.cursor = 0;
    this.planned = ok;
    this.routeVersion++;
    return ok;
  }
}


/** 월드 방향을 화면 축 입력으로(cameraYaw.screenToWorld의 역변환). main.ts가 다시 월드로 돌린다 */
export function worldToScreen(out: { x: number; z: number }, yaw: number): void {
  if (yaw === 0) return;
  const c = Math.cos(yaw), s = Math.sin(yaw), x = out.x, z = out.z;
  out.x = x * c - z * s;
  out.z = x * s + z * c;
}

